import parse from 'html-react-parser'
import { Card, Heading, Icon } from '@boltenergy-be/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ProductType } from 'types/types'
import { MeterReadingSource } from 'types/contracts'
import dayjs from 'dayjs'
import styles from './MeterReadings.module.scss'
import resources from 'translations/dutch'
import { useStoreSelector } from 'hooks/store'
import EmptyState from 'components/return-later/EmptyState.tsx'
import { isActiveContract } from 'utils/contracts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { Navigate } from 'react-router'
import { routes } from 'types/routes.ts'
import useWindowSize from 'hooks/useWindowSize.tsx'
import Link from 'components/link/Link.tsx'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import AddressSwitcherBar from 'components/address-switcher-bar/AddressSwitcherBar.tsx'
import { SMALL_DESKTOP_BREAKPOINT } from 'constants/viewport.ts'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import useGetCurrentContractMeterReadings from 'hooks/useGetCurrentContractMeterReadings'
import { Direction, DIRECTIONS, ENERGY_TYPES, TIMEFRAME_CODES, TimeframeCode } from '@boltenergy-be/shared'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import { hasEnergyTypeValue } from 'store/access-rights/utils.ts'

const MeterReadings = () => {
  // Redux store
  const { gas } = useStoreSelector(selectCurrentServiceContracts)

  // Meter readings
  const { data, isLoading } = useGetCurrentContractMeterReadings()

  // Window size
  const { isSmaller } = useWindowSize(SMALL_DESKTOP_BREAKPOINT)

  // Access rights
  const { canAccess, showContent } = useGetAccessRight(ACCESS_RIGHT_KEYS.meterReadings)

  // i18n
  const { t } = useTranslation(['consumption', 'common'])

  /**
   * Returns the correct meter reading entry based on the given product type & timeframe code
   *
   * @param {ProductType} type
   * @param {Direction} direction
   * @param {TimeframeCode} timeframeCode
   * @returns {number|undefined}
   */
  const getMeterReadingEntry = (type: ProductType, direction: Direction, timeframeCode: TimeframeCode): number | undefined => {
    if (type === ProductType.GAS) return data?.gas?.consumption?.singleRate

    switch (timeframeCode) {
      case TIMEFRAME_CODES.high:
        return data?.electricity?.[direction === DIRECTIONS.consumption ? 'consumption' : 'injection']?.doubleRate?.day

      case TIMEFRAME_CODES.low:
        return data?.electricity?.[direction === DIRECTIONS.consumption ? 'consumption' : 'injection']?.doubleRate?.night

      case TIMEFRAME_CODES.nightExclusive:
        return data?.electricity?.[direction === DIRECTIONS.consumption ? 'consumption' : 'injection']?.exclNight

      default:
        return data?.electricity?.[direction === DIRECTIONS.consumption ? 'consumption' : 'injection']?.singleRate
    }
  }

  /**
   * Returns the meter reading row for the given type, direction & timeframe code
   *
   * @param {ProductType} type
   * @param {Direction} direction
   * @param {TimeframeCode} timeframeCode
   * @returns {ReactElement}
   */
  const getMeterReadingRow = (type: ProductType, direction: Direction, timeframeCode: TimeframeCode): ReactElement => {
    const isGas = type === ProductType.GAS

    return (
      <li className={styles['meter-reading-row']}>
        <strong className={styles.type}>
          {t(($) => (!isGas && direction === DIRECTIONS.production ? $.injection : $[type]), { ns: 'common' })}
          &nbsp;
          {!isGas &&
            timeframeCode !== TIMEFRAME_CODES.totalHour &&
            t(($) => $.indexes[timeframeCode as keyof typeof resources.common.indexes], {
              ns: 'common'
            })}
        </strong>

        <small className={styles.ean}>
          {t(($) => $.units.textWithColon, {
            text: t(($) => $.ean, {
              ns: 'common'
            }),

            ns: 'common'
          })}{' '}
          {data?.[type]?.ean}
        </small>

        <data className={styles['meter-reading']} value={getMeterReadingEntry(type, direction, timeframeCode)?.toFixed(0)}>
          {getMeterReadingEntry(type, direction, timeframeCode)?.toFixed(0)}
          &nbsp;{isGas ? parse('m<sup>3</sup>') : 'kWh'}
        </data>
      </li>
    )
  }

  return !hasEnergyTypeValue(canAccess.view) ? (
    <Navigate to={routes.CONSUMPTION} replace />
  ) : !showContent ? (
    <EmptyState description={t(($) => $.meterReadings.returnLater)} />
  ) : (
    <>
      {isSmaller ? (
        <Heading as="h2" variant="h5" className={styles.title}>
          {t(($) => $.meterReadings.title)}
        </Heading>
      ) : (
        <AddressSwitcherBar className="mb-500">
          <Heading as="h2" variant="h5">
            {t(($) => $.meterReadings.title)}
          </Heading>
        </AddressSwitcherBar>
      )}

      {isLoading ? (
        <LoadingSkeleton className={styles['cards-wrapper']}>
          <LoadingSkeleton.Rectangle height={360} />
          {gas && isActiveContract(gas) && <LoadingSkeleton.Rectangle height={360} />}
        </LoadingSkeleton>
      ) : (
        <div className={styles['cards-wrapper']}>
          {canAccess.view[ENERGY_TYPES.electricity] && (
            <Card as="section" className={styles['last-meter-readings']}>
              <header className={styles['title-section']}>
                <Heading as="h2" variant="h5" serif>
                  {t(($) => $.electricity, {
                    ns: 'common'
                  })}
                </Heading>
                <Icon name="electricity" />
                <small>
                  {parse(
                    t(
                      ($) =>
                        $.meterReadings.lastMeterReadings[
                          data?.electricity?.source === MeterReadingSource.CLIENT_PROVIDED ? 'self' : 'networkOperator'
                        ],
                      {
                        date: dayjs(data?.electricity?.date).format('DD/MM/YYYY')
                      }
                    )
                  )}
                </small>
              </header>

              {data?.electricity && (
                <ul className={styles['table-section']}>
                  {/* CONSUMPTION ELECTRICITY INPUTS */}
                  {typeof data?.electricity?.consumption.singleRate !== 'undefined' &&
                    getMeterReadingRow(ProductType.ELECTRICITY, DIRECTIONS.consumption, TIMEFRAME_CODES.totalHour)}
                  {typeof data?.electricity?.consumption.doubleRate?.day !== 'undefined' &&
                    getMeterReadingRow(ProductType.ELECTRICITY, DIRECTIONS.consumption, TIMEFRAME_CODES.high)}
                  {typeof data?.electricity?.consumption.doubleRate?.night !== 'undefined' &&
                    getMeterReadingRow(ProductType.ELECTRICITY, DIRECTIONS.consumption, TIMEFRAME_CODES.low)}
                  {typeof data?.electricity?.consumption.exclNight !== 'undefined' &&
                    getMeterReadingRow(ProductType.ELECTRICITY, DIRECTIONS.consumption, TIMEFRAME_CODES.nightExclusive)}

                  {/* INJECTION ELECTRICITY INPUTS */}
                  {typeof data?.electricity?.injection?.singleRate !== 'undefined' &&
                    getMeterReadingRow(ProductType.ELECTRICITY, DIRECTIONS.production, TIMEFRAME_CODES.totalHour)}
                  {typeof data?.electricity?.injection?.doubleRate?.day !== 'undefined' &&
                    getMeterReadingRow(ProductType.ELECTRICITY, DIRECTIONS.production, TIMEFRAME_CODES.high)}
                  {typeof data?.electricity?.injection?.doubleRate?.night !== 'undefined' &&
                    getMeterReadingRow(ProductType.ELECTRICITY, DIRECTIONS.production, TIMEFRAME_CODES.low)}
                </ul>
              )}
            </Card>
          )}

          {data?.gas && canAccess.view[ENERGY_TYPES.gas] && (
            <Card as="section" className={styles['last-meter-readings']}>
              <header className={styles['title-section']}>
                <Heading as="h2" variant="h5" serif>
                  {t(($) => $.gas, {
                    ns: 'common'
                  })}
                </Heading>
                <Icon name="gas" />
                <small>
                  {parse(
                    t(
                      ($) =>
                        $.meterReadings.lastMeterReadings[
                          data.gas!.source === MeterReadingSource.CLIENT_PROVIDED ? 'self' : 'networkOperator'
                        ],
                      {
                        date: dayjs(data.gas.date).format('DD/MM/YYYY')
                      }
                    )
                  )}
                </small>
              </header>

              <ul className={styles['table-section']}>
                {/* CONSUMPTION GAS INPUTS */}
                {typeof data.gas?.consumption.singleRate !== 'undefined' &&
                  getMeterReadingRow(ProductType.GAS, DIRECTIONS.consumption, TIMEFRAME_CODES.totalHour)}
              </ul>
            </Card>
          )}

          {hasEnergyTypeValue(canAccess.edit) && (
            <Link
              className="grid-col-full"
              representation="button"
              size="large"
              href={routes.CONSUMPTION_EDIT_METER_READINGS}
              onClick={() =>
                trackEvent({ category: 'meter_readings', object: 'details', action: 'view' }, { route: routes.CONSUMPTION_METER_READINGS })
              }
            >
              {t(($) => $.meterReadings.button)}
            </Link>
          )}

          <Card as="section" color="soft" className="grid-col-full">
            <Card.Title as="h2">
              {t(($) => $.moveBanner.title, {
                ns: 'common'
              })}
            </Card.Title>
            <p>
              {parse(
                t(($) => $.moveBanner.description, {
                  ns: 'common'
                })
              )}
            </p>
            <Link
              variant="secondary"
              href={routes.MOVE}
              onNavigate={() => trackEvent({ category: 'move', object: 'banner', action: 'click' })}
            >
              {t(($) => $.moveBanner.linkText, {
                ns: 'common'
              })}
            </Link>
          </Card>
        </div>
      )}
    </>
  )
}

export default MeterReadings
